/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, March 15th 2022, 1:38:00 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { isNumber, isNaN } from "lodash";
import { FormField, FIELD_TYPES } from "lib/schemas";
import TextField from "@mui/material/TextField";
import { OSFormInputProps, useControlledInput } from "ui-widgets/forms";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { COLORSCHEME } from "theme/styles";
import Typography from "@mui/material/Typography";
import pluralize from "pluralize";

/**
 * This DateMinMaxSettings component that creates a string in a format that the backend stores for the OneStop
 * form renderer to set a start and end Date using moment.js. The string is in the format 'amount time_unit ' e.g. '1 days',
 * '2 years', '1 months', '1 month' etc. Our legacy javascript code on the django frontend splits this and uses
 * the momentjs add function to set a start and end date on the datepicker calendar
 *
 * https://momentjs.com/docs/#/manipulating/add/
 *
 * Note: MomentJs is no longer undergoing development, so this whole functionality will most likely change.
 */

interface DateMinMaxSettingsProps extends OSFormInputProps<FormField> {
  field_type: FIELD_TYPES.DATE | FIELD_TYPES.DATE_TIME;
  limitationType: "max" | "min";
}

enum DateLimitationUnit {
  years = "Years",
  months = "Months",
  days = "Days",
}

enum TimeLimitationUnit {
  hours = "Hours",
  minutes = "Minutes",
  seconds = "Seconds",
}

type LimitationUnit = keyof typeof DateLimitationUnit | keyof typeof TimeLimitationUnit;

const resolveValue = (limitationUnit: LimitationUnit, limitationValue: number) => {
  if (isNumber(limitationValue) && !isNaN(limitationValue) && limitationUnit)
    return `${limitationValue} ${limitationUnit}`;
  return null;
};

const getLimitationSettingsFromValue = (value: string): [string, LimitationUnit] =>
  (value?.split(" ") ?? []) as unknown as [string, LimitationUnit];

const useMinMaxDateSettings = (inputFieldProps: Pick<OSFormInputProps<FormField>, "schema_field_key">) => {
  const {
    field: { onChange, value },
  } = useControlledInput<FormField>(inputFieldProps);

  const defaultLimitationSettings = getLimitationSettingsFromValue(value as string);

  const [defaultLimitationValue, defaultLimitationUnit] = defaultLimitationSettings;

  // Note: MomentJS accepts either plural or singular in the add function (https://momentjs.com/docs/#/manipulating/add/)
  // So to provide a better user experience we keep our options as plural only and pluralize the value we get from
  // the backend just in case one decided make a change in django admin or db using singular units.
  // MomentJS also supports shorthands e.g. d instead of days, m instead of months. If shorthands have been used to
  // construct the max/min value in the backend, then this will not work as expected, so just change that value in the backend accordingly.
  const [limitationUnit, SetLimitationUnit] = React.useState<LimitationUnit>(
    defaultLimitationUnit ? pluralize(defaultLimitationUnit) : "years"
  );

  const [limitationValue, SetLimitationValue] = React.useState<number>(parseInt(defaultLimitationValue));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => onChange(resolveValue(limitationUnit, limitationValue)), [limitationUnit, limitationValue]);

  return { limitationUnit, SetLimitationUnit, limitationValue, SetLimitationValue };
};

export const DateMinMaxSettings = ({ field_type, limitationType, ...inputFieldProps }: DateMinMaxSettingsProps) => {
  const { limitationUnit, SetLimitationUnit, limitationValue, SetLimitationValue } =
    useMinMaxDateSettings(inputFieldProps);

  const widgetTitle =
    limitationType === "max" ? "Settings for latest possible value" : "Settings for earliest possible value";

  const limitationTextPrefix = `Limit ${limitationType === "max" ? "latest" : "earliest"} possible ${
    field_type === FIELD_TYPES.DATE_TIME ? "date/time" : "date"
  } to`;

  const limitationTextSuffix = `from day of entry.`;

  const onChangeLimitationValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    SetLimitationValue(parseInt(event.target.value));
  };

  const onChangeLimitationUnit = (event: React.ChangeEvent<HTMLInputElement>) => {
    SetLimitationUnit(event.target.value as LimitationUnit);
  };

  return (
    <>
      <FormControl>
        <Typography variant="subtitle1" gutterBottom component="div" color={COLORSCHEME.primary}>
          {widgetTitle}
        </Typography>
      </FormControl>
      <FormControl>
        <Stack direction="row" spacing={2}>
          <div>{limitationTextPrefix}</div>
          <div>
            <Box sx={{ width: "10ch" }}>
              <TextField
                className="os-date-limitation-value-setting"
                onChange={onChangeLimitationValue}
                value={limitationValue}
                type="number"
                variant="standard"
              />
            </Box>
          </div>
          <div>
            <Select
              className="os-date-limitation-value-setting"
              value={limitationUnit}
              onChange={onChangeLimitationUnit}
              variant="standard"
            >
              <MenuItem value="years">Years</MenuItem>
              <MenuItem value="months">Months</MenuItem>
              <MenuItem value="days">Days</MenuItem>
              {field_type === FIELD_TYPES.DATE_TIME && (
                <>
                  <MenuItem value="hours">Hours</MenuItem>
                  <MenuItem value="minutes">Minutes</MenuItem>
                  <MenuItem value="seconds">Seconds</MenuItem>
                </>
              )}
            </Select>
          </div>
          <div>{limitationTextSuffix}</div>
        </Stack>
      </FormControl>
    </>
  );
};

export const DOBMinMaxAgeSettings = () => {
  const { limitationValue: maximumDateValue, SetLimitationValue: setMaximumDateValue } = useMinMaxDateSettings({
    schema_field_key: "max_value",
  });

  const { limitationValue: minimumDateValue, SetLimitationValue: setMinimumDateValue } = useMinMaxDateSettings({
    schema_field_key: "min_value",
  });

  const [maxAge, setMaxAge] = React.useState<number>(-1 * minimumDateValue);
  const [minAge, setMinAge] = React.useState<number>(-1 * maximumDateValue);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => typeof maxAge === "number" && setMinimumDateValue(-1 * maxAge), [maxAge]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => typeof minAge === "number" && setMaximumDateValue(-1 * minAge), [minAge]);

  const onChangeMaxAge = (event: React.ChangeEvent<HTMLInputElement>) => setMaxAge(parseInt(event.target.value));

  const onChangeMinAge = (event: React.ChangeEvent<HTMLInputElement>) => setMinAge(parseInt(event.target.value));

  return (
    <>
      <FormControl>
        <TextField
          onChange={onChangeMaxAge}
          value={maxAge}
          label="Maximum Age"
          helperText="The maximum age in years."
          type="number"
        />
      </FormControl>
      <FormControl>
        <TextField
          onChange={onChangeMinAge}
          value={minAge}
          label="Minimum Age"
          helperText="The minimum age in years."
          type="number"
        />
      </FormControl>
    </>
  );
};
